import React, { useState, useEffect } from 'react';
import { publicApi } from '@/lib/api';
import { ChefHat, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function resolveUrl(u) {
  if (!u) return null;
  if (u.startsWith('http')) return u;
  if (u.startsWith('/')) return `${API}${u}`;
  return u;
}

export default function FormulesDuJour() {
  const [formules, setFormules] = useState([]);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    publicApi.getMenu().then(res => setFormules((res.data?.formules || []).filter(f => f.active !== false))).catch(() => {});
    publicApi.getSettings().then(res => setSettings(res.data || {})).catch(() => {});
  }, []);

  const featuredIds = settings.featured_formules || [];
  let displayed = [];
  if (featuredIds.length > 0) {
    displayed = formules.filter(f => featuredIds.includes(f.id)).slice(0, 3);
  } else {
    displayed = [...formules].sort((a, b) => (a.order || 0) - (b.order || 0) || (a.price || 0) - (b.price || 0)).slice(0, 3);
  }

  if (displayed.length === 0) return null; // FIX-HAUTE-02

  const minPrice = Math.min(...displayed.map(f => f.price || 0));
  const discoveryBadge = settings.formule_discovery_badge || 'Formule Decouverte';
  const sectionTitle = settings.formules_section_title || 'Nos Formules';
  const sectionSubtitle = settings.formules_section_subtitle || 'Cuisine maison, produits frais du marche';
  const fmEnabled = settings.formules_fait_maison_enabled !== false;
  const fmLabel = settings.formules_fait_maison_label || 'Fait Maison';
  const fmText = settings.formules_fait_maison_text || '';

  return (
    <section className="formules-section py-16 md:py-24 px-4" style={{ background: 'linear-gradient(180deg,#f9f5ef 0%,#fdf8f0 100%)' }} data-testid="formules-du-jour-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10 md:mb-14">
          <p className="text-garden-600 font-semibold tracking-[0.18em] uppercase text-xs md:text-sm mb-3">{sectionTitle}</p>
          <h2 className="font-heading font-bold text-garden-800 text-3xl md:text-4xl lg:text-5xl mb-3">Formules du Jour</h2>
          <p className="text-charcoal/70 text-sm md:text-base max-w-xl mx-auto">{sectionSubtitle}</p>
          {fmEnabled && (
            <div className="inline-flex items-center gap-2 mt-5 bg-sunny/25 border border-sunny/60 text-garden-800 px-4 py-2 rounded-full text-sm font-medium shadow-sm" data-testid="fait-maison-badge">
              <ChefHat size={16} className="text-garden-700" strokeWidth={2.2} />
              <span className="font-semibold">{fmLabel}</span>
              {fmText && <span className="text-charcoal/70 hidden sm:inline">— {fmText}</span>}
            </div>
          )}
        </div>
        <div className="grid gap-5 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {displayed.map((f) => {
            const isCheapest = minPrice !== null && f.price === minPrice;
            const highlight = isCheapest || (f.name || '').toLowerCase().includes('plat du jour');
            const ardoiseUrl = resolveUrl(f.ardoiseUrl);
            const dayText = (f.platDuJour || '').trim();
            return (
              <article key={f.id} data-testid={`formule-${f.id}`} className={`relative flex flex-col rounded-3xl p-6 sm:p-7 transition-transform hover:-translate-y-1 ${highlight ? 'bg-garden-800 text-white shadow-2xl sm:scale-[1.03]' : 'bg-white text-charcoal border border-garden-100 shadow-md'}`}>
                {highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-sunny text-charcoal text-[11px] font-bold px-3 py-1 rounded-full tracking-wider uppercase whitespace-nowrap shadow-sm">
                    {discoveryBadge}
                  </div>
                )}
                <div className="mb-4">
                  <div className={`text-5xl md:text-6xl font-heading font-bold leading-none ${highlight ? 'text-sunny' : 'text-garden-700'}`}>
                    {f.price}<span className="text-2xl md:text-3xl align-top ml-1">€</span>
                  </div>
                  <h3 className={`mt-3 font-heading font-semibold text-xl ${highlight ? 'text-white' : 'text-garden-800'}`}>{f.name}</h3>
                  {f.description && <p className={`mt-1 text-sm leading-snug ${highlight ? 'text-white/80' : 'text-charcoal/60'}`}>{f.description}</p>}
                </div>
                {f.items?.length > 0 && (
                  <ul className="space-y-1.5 mb-4">
                    {f.items.map((item, idx) => (
                      <li key={idx} className={`flex items-start gap-2 text-sm ${highlight ? 'text-white/90' : 'text-charcoal/80'}`}>
                        <Check size={15} className={`mt-0.5 shrink-0 ${highlight ? 'text-sunny' : 'text-garden-600'}`} strokeWidth={2.5} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {ardoiseUrl ? (
                  <div className="mt-auto -mx-1">
                    <div className="rounded-2xl overflow-hidden border border-black/5 bg-cream/40">
                      <img src={ardoiseUrl} alt={`Ardoise – ${f.name}`} loading="lazy" decoding="async" className="w-full h-44 sm:h-48 object-cover" />
                    </div>
                    {dayText && (
                      <div className={`mt-2 text-xs leading-snug px-1 ${highlight ? 'text-white/75' : 'text-charcoal/60'}`}>
                        {dayText.split('\n').map((line, i) => <div key={i}>{line}</div>)}
                      </div>
                    )}
                  </div>
                ) : dayText ? (
                  <div className={`mt-auto rounded-2xl p-3 text-sm leading-relaxed ${highlight ? 'bg-white/10 text-white/95' : 'bg-cream/60 text-charcoal/80'}`}>
                    {dayText.split('\n').map((line, i) => <div key={i}>{line}</div>)}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
